import { MutableRefObject } from 'react';
import { useQuery, QueryFunctionContext, QueryKey } from '@tanstack/react-query';

import type { Board, StickerDesign, StickerDesignLookupTable , ExpandedSlap} from '../utils/Primitives';
import { normalizeBaseUrl, sanitizeEnvUrl } from '../utils/shareLinks';

export function getStickerUrl(sticker: StickerDesign) {
    const IPFSGateway = sanitizeEnvUrl(process.env.NEXT_PUBLIC_IPFS_GATEWAY)
    if (IPFSGateway && sticker.imageCID) {
        return `${normalizeBaseUrl(IPFSGateway)}/${sticker.imageCID}`
    }
    if (sticker.filename) {
        return `/img/${sticker.filename}`
    }
    return `/img/sticker-ex1.png?hmm`
}

export function getGatewayUrlForCID(cid: string) : string | null {
    const IPFSGateway = sanitizeEnvUrl(process.env.NEXT_PUBLIC_IPFS_GATEWAY)
    if (IPFSGateway && cid) {
        return `${normalizeBaseUrl(IPFSGateway)}/${cid}`
    }
    return null
}

export function getUrlForCID(cid: string | null) : string {
    const IPFSGateway = sanitizeEnvUrl(process.env.NEXT_PUBLIC_IPFS_GATEWAY)
    if (IPFSGateway && cid) {
        return `${normalizeBaseUrl(IPFSGateway)}/${cid}`
    }
    return `/img/sticker-ex1.png?hmm`
}

export function getStickerDesigns(props: {boardRef: MutableRefObject<Board>}): StickerDesignLookupTable {
    const { boardRef } = props
    const stickerStore = boardRef.current.stickerDesignStore as StickerDesignLookupTable
    const stickerDesigns = Object.values(stickerStore)
    return stickerDesigns
}

export function getLatestStickerDesigns(props: {boardRef: MutableRefObject<Board>, count: number}): StickerDesign[] {
    const { boardRef, count } = props;
    const fetchedStickerDesigns: StickerDesignLookupTable = getStickerDesigns({ boardRef });
    const stickerArray: StickerDesign[] = Object.values(fetchedStickerDesigns).filter((sticker): sticker is StickerDesign => sticker !== null);

    // Sort the stickers by publishedAt date in descending order
    stickerArray.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

    // Return the latest 'count' number of stickers
    return stickerArray.slice(0, count);
}

export function getMostSlappedStickerDesigns(props: {boardRef: MutableRefObject<Board>, count: number}): StickerDesign[] {
    const { boardRef, count } = props;
    const fetchedStickerDesigns: StickerDesignLookupTable = getStickerDesigns({ boardRef });
    const stickerArray: StickerDesign[] = Object.values(fetchedStickerDesigns).filter((sticker): sticker is StickerDesign => sticker !== null);

    // Sort the stickers by slapCount in descending order
    stickerArray.sort((a, b) => (b.slapCount ?? 0) - (a.slapCount ?? 0));

    // Return the top 'count' number of stickers with the highest slapCount
    return stickerArray.slice(0, count);
}

export type useFetchSaveLatestStickerDesignsReturn = {
    data: StickerDesign[],
    isLoading: boolean,
    isFetched: boolean
}

export function useFetchSaveLatestStickerDesigns(props: {boardRef: MutableRefObject<Board>, count: number}): useFetchSaveLatestStickerDesignsReturn {
    const { boardRef, count } = props

    const { data, isLoading, isFetched } = useQuery({
        queryKey: ['stickerDesigns'],
        queryFn: async () => {
            const StickercloudApiUrl = process.env.NEXT_PUBLIC_STICKERCLOUD_API as string
            if (!StickercloudApiUrl || StickercloudApiUrl.length === 0) {
                console.error(`Stickercloud API URL is not set`)
                return [] as StickerDesign[];
            }
            const stickerDesignsUrl = `${StickercloudApiUrl}/sticker-designs`
            const response = await fetch(stickerDesignsUrl);

            if (!response.ok) {
              throw new Error(`Failed to fetch data: ${response.statusText}`);
            }
            const responseText = await response.text()
            const jsonFull = JSON.parse(responseText);
            if (!jsonFull['data'] || jsonFull['data'].length === 0) {
                return [] as StickerDesign[];
            }
            let stickerDesigns = [] as StickerDesign[]
            for (const sticker of jsonFull['data']) {
                const metadataLoaded = sticker['metadataLoaded'] as boolean
                if (!metadataLoaded) {
                    console.log(`skipping sticker design ${sticker['stickerId']} because metadata is not loaded`)
                    continue
                }
                const resolvedStickerId = Number(sticker['stickerId'] ?? sticker['id']);
                if (!Number.isFinite(resolvedStickerId)) {
                    continue;
                }
                const loadedStickerDesign = {
                        id: resolvedStickerId,
                        name: sticker['name'],
                        originalPublisher: sticker['originalPublisher'],
                        originalPublisherEns: sticker['originalPublisherEns'],
                        currentPublisher: sticker['currentPublisher'],
                        currentPublisherEns: sticker['currentPublisherEns'],
                        payoutAddress: sticker['payoutAddress'],
                        publishedAt: sticker['publishedAt'],
                        price: sticker['price'],
                        paymentMethodId: sticker['paymentMethodId'],
                        limit: sticker['limit'],
                        endTime: sticker['endTime'],
                        aspectRatio: Number(sticker['aspectRatio']),
                        imageCID: sticker['imageCID'],
                        metaCID: sticker['metadataCID'],
                        filename: sticker['filename'],
                        description: sticker['description'],
                        url: sticker['url'],
                        slapCount: sticker['slapCount'],
                    } as StickerDesign
                writeStickerDesignToBoard({boardRef, sticker: loadedStickerDesign})
                stickerDesigns.push(loadedStickerDesign)
            }
            console.log(`Fetched and saved ${stickerDesigns.length} sticker designs`)
            return stickerDesigns;
          },
    })

    const result = {
        data: data as StickerDesign[],
        isLoading: isLoading,
        isFetched: isFetched
    } as useFetchSaveLatestStickerDesignsReturn
    return result
}


export function getStickerDesign(props: {
    boardRef: MutableRefObject<Board>,
    stickerId: number;
}): StickerDesign | null {
    const { boardRef, stickerId } = props
    const stickerStore = boardRef.current.stickerDesignStore as StickerDesignLookupTable
    const foundStickerDesign = stickerStore[stickerId]
    if (foundStickerDesign) {
        return foundStickerDesign
    }
    return null
}

export function getNextStickerId(props: {boardRef: MutableRefObject<Board>}): number {
    const { boardRef } = props
    const stickerStore = boardRef.current.stickerDesignStore as StickerDesignLookupTable
    const stickerIds = Object.keys(stickerStore)
    const nextStickerId = stickerIds.length + 1
    return nextStickerId
}

export const writeStickerDesignToBoard = (props: {boardRef: MutableRefObject<Board>, sticker: StickerDesign}) => {
    const { boardRef, sticker } = props
    const writeStickerId = sticker.id as number
    const stickerStore = boardRef.current.stickerDesignStore as StickerDesignLookupTable
    stickerStore[writeStickerId] = sticker
}

export async function getOrLoadStickerDesign(props: {
    boardRef: MutableRefObject<Board>,
    stickerId: number;
}): Promise<StickerDesign | null> {
    const { boardRef, stickerId } = props;

    // First, try to get the sticker design from local state
    const localStickerDesign = getStickerDesign({ boardRef, stickerId });
    if (localStickerDesign) {
        return localStickerDesign;
    }

    // If not found locally, fetch from API
    const StickercloudApiUrl = process.env.NEXT_PUBLIC_STICKERCLOUD_API as string;
    if (!StickercloudApiUrl || StickercloudApiUrl.length === 0) {
        console.error(`Stickercloud API URL is not set`);
        return null;
    }

    const stickerDesignUrl = `${StickercloudApiUrl}sticker-designs?id=${stickerId}`;
    console.log(`[DEBUG] getOrLoadStickerDesign: Fetching from URL: ${stickerDesignUrl}`);

    try {
        const response = await fetch(stickerDesignUrl);
        if (!response.ok) {
            console.error(`[DEBUG] getOrLoadStickerDesign: Failed to fetch sticker design: ${response.statusText}`);
            throw new Error(`Failed to fetch sticker design: ${response.statusText}`);
        }

        const jsonData = await response.json();
        console.log(`[DEBUG] getOrLoadStickerDesign: Received data for stickerId ${stickerId}:`, jsonData);

        if (!jsonData['data'] || jsonData['data'].length === 0) {
            console.log(`[DEBUG] getOrLoadStickerDesign: No data found for stickerId ${stickerId}`);
            return null;
        }

        const stickerData = jsonData['data'][0];
        if (!stickerData['metadataLoaded']) {
            console.log(`Skipping sticker design ${stickerId} because metadata is not loaded`);
            return null;
        }

        const resolvedStickerId = Number(stickerData['stickerId'] ?? stickerData['id'] ?? stickerId);
        const resolvedPaymentMethodId = Number(stickerData['paymentMethodId']);

        const loadedStickerDesign: StickerDesign = {
            id: resolvedStickerId,
            name: stickerData['name'],
            originalPublisher: stickerData['originalPublisher'],
            originalPublisherEns: stickerData['originalPublisherEns'],
            currentPublisher: stickerData['currentPublisher'],
            currentPublisherEns: stickerData['currentPublisherEns'],
            payoutAddress: stickerData['payoutAddress'],
            publishedAt: stickerData['publishedAt'],
            price: stickerData['price'],
            paymentMethodId: resolvedPaymentMethodId,
            limit: stickerData['limit'],
            endTime: stickerData['endTime'],
            aspectRatio: Number(stickerData['aspectRatio']),
            imageCID: stickerData['imageCID'],
            metaCID: stickerData['metadataCID'],
            filename: stickerData['filename'],
            description: stickerData['description'],
            url: stickerData['url'],
            slapCount: stickerData['slapCount'],
        };

        console.log(`[DEBUG] getOrLoadStickerDesign: Created StickerDesign object for id ${stickerId}:`, loadedStickerDesign);

        // Store the fetched sticker design in the Board
        writeStickerDesignToBoard({ boardRef, sticker: loadedStickerDesign });
        console.log(`[DEBUG] getOrLoadStickerDesign: Stored sticker design ${stickerId} in Board`);

        return loadedStickerDesign;
    } catch (error) {
        console.error(`Error fetching sticker design ${stickerId}:`, error);
        return null;
    }
}
